/**
 * The wire format of a server-sent event stream, as a function over strings.
 *
 * **This is a parser rather than an `EventSource`, and that is the whole of the transport
 * decision.** The platform's `EventSource` would hand back parsed frames for free, and it is the
 * wrong tool here for one reason that outweighs everything it saves: it cannot report the HTTP
 * status. A stream refused `401` or `403` — an expired session, a role that lost `presence:read`,
 * an operator whose account was disabled mid-shift — is retried by `EventSource` every few seconds,
 * forever, with no way for the page to see why. That traffic lands on the coarse per-address tier
 * that every terminal behind the building's address shares with the gates' own scans, so a terminal
 * nobody is using could spend the budget a queue of people needs. Going through `fetch` means the status is readable, the retry
 * is ours, and an abort is distinguishable from a failure.
 *
 * **It keeps a buffer because a frame is not a chunk.** A `ReadableStream` splits wherever the
 * network did: one read can carry half a frame, three frames, or a frame and a half. Frames are
 * separated by a blank line and nothing else, so what has not been terminated yet is held until it
 * is.
 *
 * **A line this does not recognise is ignored rather than refused**, which is what the format asks
 * for and what makes the stream extensible: the hub writes `: keepalive` comments on a timer for
 * the benefit of the two Nginx layers, and a client that treated one as a protocol error would
 * disconnect every fifteen seconds on a healthy connection.
 */

/** One decoded frame: the `event:` name, and the `data:` lines joined as the format requires. */
export interface SseFrame {
  readonly event: string;
  readonly data: string;
}

/**
 * Feeds decoded text in and gets whole frames out.
 *
 * Stateful because the buffer is, and a closure rather than a class because there is exactly one
 * thing to call.
 */
/** One `field: value` line, split where the format says to split it. */
function fieldOf(line: string): { readonly field: string; readonly value: string } {
  const colon = line.indexOf(':');
  if (colon === -1) {
    return { field: line, value: '' };
  }
  // One optional space after the colon belongs to the format, not to the value.
  const start = line[colon + 1] === ' ' ? colon + 2 : colon + 1;
  return { field: line.slice(0, colon), value: line.slice(start) };
}

/**
 * One complete block between blank lines, as a frame, or `null` when it carried no data.
 *
 * `id` and `retry` are deliberately dropped. The server emits no `id:` line, ever — that is what
 * makes "no resume" structural rather than merely unimplemented — so reading one would be reading
 * something this product does not send.
 */
function frameOf(block: string): SseFrame | null {
  let event = 'message';
  const data: string[] = [];

  for (const line of block.split('\n')) {
    if (line.length === 0 || line.startsWith(':')) {
      // A comment. The heartbeat is one, and it exists for the proxies rather than for us.
      continue;
    }
    const { field, value } = fieldOf(line);
    if (field === 'event') {
      event = value;
    } else if (field === 'data') {
      data.push(value);
    }
  }

  return data.length === 0 ? null : { event, data: data.join('\n') };
}

export function createSseParser(): {
  readonly push: (chunk: string) => readonly SseFrame[];
} {
  let buffer = '';

  return {
    push(chunk: string): readonly SseFrame[] {
      // `\r\n` is legal in the format and no intermediary here is known to rewrite line endings,
      // but normalising costs one pass and removes the question.
      buffer += chunk.replaceAll('\r\n', '\n').replaceAll('\r', '\n');
      const frames: SseFrame[] = [];

      for (;;) {
        const boundary = buffer.indexOf('\n\n');
        if (boundary === -1) {
          return frames;
        }
        const frame = frameOf(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (frame !== null) {
          frames.push(frame);
        }
      }
    },
  };
}
